import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useManagerTheme } from '../context/ManagerThemeContext';
import { authAPI } from '../services/api';
import ManagerSidebar from '../components/ManagerSidebar';
import ManagerHeader from '../components/ManagerHeader';

const HEX_REGEX = /^#[0-9A-Fa-f]{6}$/;

const ManagerSettings: React.FC = () => {
  const { user, logout } = useAuth();
  const {
    sidebarColor,
    headerColor,
    contentColor,
    sectionColor,
    tableColor,
    setTheme,
  } = useManagerTheme();
  const [showMobileSidebar, setShowMobileSidebar] = useState(false);
  const [sidebarInput, setSidebarInput] = useState(sidebarColor);
  const [headerInput, setHeaderInput] = useState(headerColor);
  const [contentInput, setContentInput] = useState(contentColor);
  const [sectionInput, setSectionInput] = useState(sectionColor);
  const [tableInput, setTableInput] = useState(tableColor);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    setSidebarInput(sidebarColor);
    setHeaderInput(headerColor);
    setContentInput(contentColor);
    setSectionInput(sectionColor);
    setTableInput(tableColor);
  }, [sidebarColor, headerColor, contentColor, sectionColor, tableColor]);

  const handleSave = async () => {
    setSaveError(null);
    const next = {
      sidebarColor: HEX_REGEX.test(sidebarInput) ? sidebarInput : sidebarColor,
      headerColor: HEX_REGEX.test(headerInput) ? headerInput : headerColor,
      contentColor: HEX_REGEX.test(contentInput) ? contentInput : contentColor,
      sectionColor: HEX_REGEX.test(sectionInput) ? sectionInput : sectionColor,
      tableColor: HEX_REGEX.test(tableInput) ? tableInput : tableColor,
    };
    setTheme(next);
    setSidebarInput(next.sidebarColor);
    setHeaderInput(next.headerColor);
    setContentInput(next.contentColor);
    setSectionInput(next.sectionColor);
    setTableInput(next.tableColor);
    try {
      await authAPI.updateTheme(next);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err: unknown) {
      const message = err && typeof err === 'object' && 'response' in err
        ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
        : 'Failed to save theme';
      setSaveError(message || 'Failed to save theme. Your colors are applied locally.');
    }
  };

  return (
    <div className="flex flex-col min-h-screen w-full overflow-x-hidden text-slate-200" style={{ backgroundColor: contentColor }}>
      <button
        type="button"
        className="hidden max-md:flex fixed top-4 right-4 z-[9999] w-11 h-11 rounded-xl border border-white/[0.08] text-slate-300 text-xl cursor-pointer shadow-lg items-center justify-center hover:opacity-90 transition-colors"
        style={{ backgroundColor: sidebarColor }}
        onClick={() => setShowMobileSidebar(!showMobileSidebar)}
        aria-label="Toggle sidebar"
      >
        ☰
      </button>

      {showMobileSidebar && (
        <div
          className="fixed inset-0 bg-black/60 z-[1002] backdrop-blur-sm max-md:block hidden"
          onClick={() => setShowMobileSidebar(false)}
          aria-hidden
        />
      )}

      <div
        className={`fixed top-0 left-0 w-[280px] h-screen border-r border-white/[0.08] z-[1003] shadow-xl transition-transform duration-300 overflow-y-auto max-md:block hidden ${
          showMobileSidebar ? 'translate-x-0' : '-translate-x-full'
        }`}
        style={{ backgroundColor: sidebarColor }}
      >
        <div className="flex items-center justify-between p-5 border-b border-white/[0.08]">
          <h3 className="m-0 text-lg font-semibold text-white">Menu</h3>
          <button
            type="button"
            className="w-9 h-9 flex items-center justify-center rounded-lg text-slate-400 hover:text-white transition-colors border-none cursor-pointer opacity-80 hover:opacity-100"
            onClick={() => setShowMobileSidebar(false)}
          >
            ✕
          </button>
        </div>
        <nav className="flex flex-col py-2">
          {[
            { href: '/manager', label: 'Home', icon: '🏠' },
            { href: '/manager/account', label: 'My Account', icon: '👤' },
            { href: '/manager/payouts', label: 'Payouts', icon: '💰' },
            { href: '/manager/reports', label: 'Reports', icon: '📊' },
            { href: '/manager/merchants', label: 'My Merchants', icon: '🏪' },
            { href: '/manager/merchant-report-history', label: 'Merchant Reports', icon: '💳' },
            { href: '/manager/settings', label: 'Settings', icon: '⚙️' },
          ].map(({ href, label, icon }) => (
            <button
              key={href}
              type="button"
              className="flex items-center gap-4 py-3.5 px-5 w-full text-left border-none bg-transparent cursor-pointer transition-colors hover:opacity-90 text-slate-400 hover:text-white"
              onClick={() => { setShowMobileSidebar(false); window.location.href = href; }}
            >
              <span className="text-lg w-7 text-center">{icon}</span>
              <span className="text-sm font-medium">{label}</span>
            </button>
          ))}
        </nav>
      </div>

      <ManagerHeader
        user={user}
        onRefresh={() => window.location.reload()}
        onCreditMoney={() => {}}
        onMyProfile={() => (window.location.href = '/manager/account')}
        onLogout={() => logout()}
      />

      <div className="flex flex-1 min-h-0 w-full">
        <div className="max-md:hidden w-[280px] flex-shrink-0 flex flex-col border-r border-white/[0.08]" style={{ backgroundColor: sidebarColor }}>
          <ManagerSidebar activeTab="settings" />
        </div>

        <div className="flex-1 min-w-0 flex flex-col w-full p-6 md:p-8 text-slate-200 gap-6" style={{ backgroundColor: contentColor }}>
          <div className="border-b border-white/[0.08] pb-5">
            <h1 className="text-xl md:text-2xl font-semibold text-white m-0 mb-1 tracking-tight">Settings</h1>
            <p className="text-slate-500 text-sm m-0">Customize panel appearance. Set colors for sidebar, header, content, sections, and tables.</p>
          </div>

          <div className="p-6 rounded-2xl border border-white/[0.06] shadow-xl max-w-lg" style={{ background: 'linear-gradient(135deg, rgba(53,53,72,0.95) 0%, rgba(38,38,51,0.95) 100%)' }}>
            <h2 className="text-base font-semibold text-white m-0 mb-4">Appearance</h2>
            <div className="flex flex-col gap-6">
              <div className="flex flex-col gap-2">
                <label className="text-sm font-medium text-slate-400">Sidebar</label>
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    value={sidebarInput}
                    onChange={(e) => setSidebarInput(e.target.value)}
                    className="w-12 h-12 rounded-lg border-2 border-white/20 cursor-pointer bg-transparent"
                  />
                  <input
                    type="text"
                    value={sidebarInput}
                    onChange={(e) => setSidebarInput(e.target.value)}
                    className="px-3 py-2 rounded-lg bg-white/10 border border-white/10 text-slate-200 text-sm font-mono w-32"
                    placeholder="#1E1E28"
                  />
                </div>
              </div>
              <div className="flex flex-col gap-2">
                <label className="text-sm font-medium text-slate-400">Header</label>
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    value={headerInput}
                    onChange={(e) => setHeaderInput(e.target.value)}
                    className="w-12 h-12 rounded-lg border-2 border-white/20 cursor-pointer bg-transparent"
                  />
                  <input
                    type="text"
                    value={headerInput}
                    onChange={(e) => setHeaderInput(e.target.value)}
                    className="px-3 py-2 rounded-lg bg-white/10 border border-white/10 text-slate-200 text-sm font-mono w-32"
                    placeholder="#1E1E28"
                  />
                </div>
              </div>
              <div className="flex flex-col gap-2">
                <label className="text-sm font-medium text-slate-400">Content body</label>
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    value={contentInput}
                    onChange={(e) => setContentInput(e.target.value)}
                    className="w-12 h-12 rounded-lg border-2 border-white/20 cursor-pointer bg-transparent"
                  />
                  <input
                    type="text"
                    value={contentInput}
                    onChange={(e) => setContentInput(e.target.value)}
                    className="px-3 py-2 rounded-lg bg-white/10 border border-white/10 text-slate-200 text-sm font-mono w-32"
                    placeholder="#1E1E28"
                  />
                </div>
              </div>
              <div className="flex flex-col gap-2">
                <label className="text-sm font-medium text-slate-400">Sections background</label>
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    value={sectionInput}
                    onChange={(e) => setSectionInput(e.target.value)}
                    className="w-12 h-12 rounded-lg border-2 border-white/20 cursor-pointer bg-transparent"
                  />
                  <input
                    type="text"
                    value={sectionInput}
                    onChange={(e) => setSectionInput(e.target.value)}
                    className="px-3 py-2 rounded-lg bg-white/10 border border-white/10 text-slate-200 text-sm font-mono w-32"
                    placeholder="#353548"
                  />
                </div>
              </div>
              <div className="flex flex-col gap-2">
                <label className="text-sm font-medium text-slate-400">Table background</label>
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    value={tableInput}
                    onChange={(e) => setTableInput(e.target.value)}
                    className="w-12 h-12 rounded-lg border-2 border-white/20 cursor-pointer bg-transparent"
                  />
                  <input
                    type="text"
                    value={tableInput}
                    onChange={(e) => setTableInput(e.target.value)}
                    className="px-3 py-2 rounded-lg bg-white/10 border border-white/10 text-slate-200 text-sm font-mono w-32"
                    placeholder="#334155"
                  />
                </div>
              </div>
              <button
                type="button"
                onClick={handleSave}
                className="mt-2 py-2.5 px-5 rounded-lg font-semibold text-sm bg-gradient-to-r from-violet-500 to-purple-600 text-white border-none cursor-pointer hover:opacity-90 transition-opacity w-fit"
              >
                {saved ? 'Saved' : 'Save'}
              </button>
              {saveError && (
                <p className="mt-2 text-sm text-amber-400 m-0">{saveError}</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ManagerSettings;
